import { createContext, useContext, useEffect, useState } from "react";

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  const [clock, setClock] = useState(() => localStorage.getItem("clock") || "12h");
  const [snapToGrid, setSnapToGrid] = useState(() => localStorage.getItem("snapToGrid") !== "false");
  const [snapMinutes, setSnapMinutes] = useState(() => {
    const raw = parseInt(localStorage.getItem("snapMinutes") || "15", 10);
    return [5, 10, 15, 30, 60].includes(raw) ? raw : 15;
  });
  const [colorBlind, setColorBlind] = useState(() => localStorage.getItem("colorBlind") === "true");
  const [maxNotifications, setMaxNotifications] = useState(() => {
    const raw = parseInt(localStorage.getItem("maxNotifications") || "3", 10);
    if (!Number.isFinite(raw)) return 3;
    return Math.min(5, Math.max(1, raw));
  });
  // Week-start day (iter35 user request: "We need to make an option to choose
  // which day week starts on"). 0=Sunday, 1=Monday, 6=Saturday — passed to
  // date-fns `startOfWeek({weekStartsOn})`. Default is Monday (1).
  const [weekStartsOn, setWeekStartsOn] = useState(() => {
    const raw = parseInt(localStorage.getItem("weekStartsOn") || "1", 10);
    return [0, 1, 6].includes(raw) ? raw : 1;
  });
  // Per-weekday column tint (issue: "custom weekday column colors").
  // Shape: { Mon: "#5A90F5", Tue: null, ... }. null => no tint.
  const DEFAULT_WEEKDAY_TINTS = { Mon: null, Tue: null, Wed: null, Thu: null, Fri: null, Sat: null, Sun: null };
  const [weekdayTints, setWeekdayTints] = useState(() => {
    try { return { ...DEFAULT_WEEKDAY_TINTS, ...JSON.parse(localStorage.getItem("weekdayTints") || "{}") }; }
    catch { return DEFAULT_WEEKDAY_TINTS; }
  });
  // Weekly capacity in hours — used by the capacity bar in the calendar header.
  const [weekHours, setWeekHours] = useState(() => {
    const raw = parseInt(localStorage.getItem("weekHours") || "40", 10);
    return Number.isFinite(raw) && raw > 0 ? Math.min(168, raw) : 40;
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.documentElement.classList.toggle("light-mode", theme === "light");
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("clock", clock);
  }, [clock]);

  useEffect(() => { localStorage.setItem("snapToGrid", String(snapToGrid)); }, [snapToGrid]);
  useEffect(() => { localStorage.setItem("snapMinutes", String(snapMinutes)); }, [snapMinutes]);
  useEffect(() => {
    localStorage.setItem("colorBlind", String(colorBlind));
    document.documentElement.classList.toggle("cb-mode", colorBlind);
  }, [colorBlind]);
  useEffect(() => { localStorage.setItem("maxNotifications", String(maxNotifications)); }, [maxNotifications]);
  useEffect(() => { localStorage.setItem("weekStartsOn", String(weekStartsOn)); }, [weekStartsOn]);
  useEffect(() => { localStorage.setItem("weekdayTints", JSON.stringify(weekdayTints)); }, [weekdayTints]);
  useEffect(() => { localStorage.setItem("weekHours", String(weekHours)); }, [weekHours]);

  const timeFormat = clock === "24h" ? "HH:mm" : "h:mm a";

  return (
    <SettingsContext.Provider value={{
      theme, setTheme,
      clock, setClock, timeFormat,
      snapToGrid, setSnapToGrid,
      snapMinutes, setSnapMinutes,
      colorBlind, setColorBlind,
      maxNotifications, setMaxNotifications,
      weekStartsOn, setWeekStartsOn,
      weekdayTints, setWeekdayTints,
      weekHours, setWeekHours,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
